import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface WelcomeSplashPayload {
    firstName: string;
}

/**
 * Steuert die einmalige Begrüßungsanimation nach erfolgreichem Keycloak-Login.
 *
 * Anzeige-Regel: genau einmal pro Browser-Session (SessionStorage-Flag) oder,
 * falls SessionStorage blockiert ist, einmal pro AppComponent-Lebenszyklus
 * (In-Memory-Flag als Fallback). Der Splash darf auch ohne Vornamen
 * angezeigt werden – die Komponente entscheidet dann über die Variante.
 */
@Injectable({ providedIn: 'root' })
export class WelcomeSplashService {

    private static readonly SESSION_FLAG = 'u2b_welcome_splash_shown';

    private readonly _show$ = new BehaviorSubject<WelcomeSplashPayload | null>(null);
    private _shownInMemory = false;

    /** Stream für die Splash-Komponente; null = verbergen. */
    readonly show$: Observable<WelcomeSplashPayload | null> = this._show$.asObservable();

    /**
     * Zeigt den Splash an – aber nur, wenn er in dieser Browser-Session noch
     * nicht gezeigt wurde UND der Tab nicht durch einen internen App-Link
     * geöffnet wurde (z.B. "In neuem Tab öffnen" aus einer Detailmaske).
     *
     * `firstName` ist optional – bei leerem/fehlendem Namen zeigt die
     * Komponente eine namenslose Variante ("Guten Morgen!").
     */
    showOncePerSession(firstName?: string | null): void {
        if (this._isAlreadyShown()) {
            return;
        }

        if (this._isOpenedViaInternalLink()) {
            // Kein Splash, aber Session markieren – sonst erscheint er in weiteren Tabs
            // ohne Referrer (z. B. bei rel="noreferrer") erneut.
            this._markAsShown();
            return;
        }

        this._markAsShown();

        const normalized = (firstName ?? '').trim();
        this._show$.next({ firstName: normalized });
    }

    /**
     * Zeigt den Splash ohne Session-Check (z. B. für "Begrüß mich noch mal"-
     * Button). Setzt dabei NICHT das "bereits gezeigt"-Flag, damit der Splash
     * beim nächsten Login erneut erscheint.
     *
     * **Race Condition Hinweis:** Wenn `showNow()` kurz nach `showOncePerSession()`
     * aufgerufen wird, überschreibt es das Payload im Stream. Das Session-Flag
     * bleibt dabei aber gesetzt. Das ist gewollt: Der "Begrüß mich noch mal"-
     * Button ist ein manueller User-Trigger, der unabhängig vom Session-Zustand
     * funktionieren soll. Bei erneutem Login erscheint der Splash regulär.
     */
    showNow(firstName?: string | null): void {
        const normalized = (firstName ?? '').trim();
        this._show$.next({ firstName: normalized });
    }

    /** Wird von der Komponente aufgerufen, sobald die Exit-Animation beendet ist. */
    dismiss(): void {
        this._show$.next(null);
    }

    /**
     * Vor dem Öffnen eines internen Links in einem neuen Tab (mousedown), damit
     * der frische Tab den Welcome-Splash nicht erneut anzeigt.
     */
    prepareForInternalNewTab(): void {
        this._markAsShown();
    }

    /**
     * Setzt das "bereits gezeigt"-Flag zurück. Wird beim Logout aufgerufen,
     * damit beim nächsten Login – auch im selben Tab – der Splash erneut
     * erscheint.
     */
    resetSessionFlag(): void {
        this._shownInMemory = false;
        try {
            sessionStorage.removeItem(WelcomeSplashService.SESSION_FLAG);
        } catch {
            /* SessionStorage nicht verfügbar – In-Memory-Flag reicht */
        }
    }

    /**
     * Gibt true zurück, wenn der aktuelle Tab durch einen internen App-Link
     * geöffnet wurde (target="_blank" oder window.open() aus derselben Origin).
     * In diesem Fall soll der Splash nicht erscheinen – der Nutzer hat die App
     * bereits geöffnet und navigiert nur in einem neuen Tab weiter.
     *
     * Erkennungsmerkmal: `document.referrer` ist gesetzt und stammt von
     * derselben Origin wie die aktuelle Seite.
     */
    private _isOpenedViaInternalLink(): boolean {
        try {
            const referrer = document.referrer;
            if (!referrer) {
                return false;
            }
            return new URL(referrer).origin === window.location.origin;
        } catch {
            return false;
        }
    }

    private _isAlreadyShown(): boolean {
        if (this._shownInMemory) {
            return true;
        }
        try {
            return sessionStorage.getItem(WelcomeSplashService.SESSION_FLAG) === '1';
        } catch {
            return false;
        }
    }

    private _markAsShown(): void {
        this._shownInMemory = true;
        try {
            sessionStorage.setItem(WelcomeSplashService.SESSION_FLAG, '1');
        } catch {
            /* SessionStorage nicht verfügbar – In-Memory-Flag greift */
        }
    }
}
